import { useCallback, useEffect, useState, useMemo } from 'react'
import { Checkbox, List, Tag, Button, Tooltip, Flex } from 'antd'
import {
  StarOutlined,
  CloseOutlined,
  InfoCircleOutlined,
  ExclamationCircleOutlined,
  DownOutlined,
  UpOutlined,
  LeftOutlined,
} from '@ant-design/icons'
import PropTypes from 'prop-types'
import dayjs from 'dayjs'
import { createUseStyles, useTheme } from 'react-jss'
import cn from 'classnames'

import { useAsync } from '../../hooks'
import api from '../../api'
import { formatMedicalProvider } from '../Forms/MedicalProviderSelect'
import CircleCheckIcon from './CircleCheckIcon'
import Copy from './Copy'

const REPORTS = {
  remoteChecks: 'Remote checks',
  receivedTransmissions: 'Transmissions',
  officeChecks: 'Office checks',
  alerts: 'Alerts',
}

const getLink = entry => {
  if (entry.type === 'alerts') return `/alerts/${entry.id}`
  if (entry.type === 'receivedTransmissions')
    return `/transmissions/${entry.id}`
  if (entry.type === 'officeChecks') return `/office-checks/${entry.id}`
  if (entry.type === 'remoteChecks') return `/remote-checks/${entry.id}`
  return ''
}

function SidebarBooleanTag({ enabled = false, label, tooltip, classes }) {
  return (
    <div className={classes.alertRow}>
      <CircleCheckIcon
        checked={enabled}
        className={classes[enabled ? 'ok' : 'notOk']}
      />{' '}
      <span className={classes.alertLabel}>{label}</span>
      <Tooltip title={tooltip}>
        <InfoCircleOutlined className={classes.alertInfo} />
      </Tooltip>
    </div>
  )
}

SidebarBooleanTag.propTypes = {
  enabled: PropTypes.bool,
  label: PropTypes.string.isRequired,
  tooltip: PropTypes.string.isRequired,
  classes: PropTypes.shape({
    alertRow: PropTypes.string.isRequired,
    alertLabel: PropTypes.string.isRequired,
    alertInfo: PropTypes.string.isRequired,
    ok: PropTypes.string.isRequired,
    notOk: PropTypes.string.isRequired,
  }).isRequired,
}
function SidebarBooleanIconRow({ enabled = false, label, classes }) {
  return (
    <div>
      <CircleCheckIcon
        checked={enabled}
        className={classes[enabled ? 'ok' : 'notOk']}
      />{' '}
      {label}
    </div>
  )
}

SidebarBooleanIconRow.propTypes = {
  enabled: PropTypes.bool,
  label: PropTypes.string.isRequired,
  classes: PropTypes.shape({
    alertRow: PropTypes.string.isRequired,
    iconRowLabel: PropTypes.string.isRequired,
    ok: PropTypes.string.isRequired,
    notOk: PropTypes.string.isRequired,
  }).isRequired,
}
function TransmissionSidebar({
  patient,
  practiceId,
  remoteCheckId = null,
  includedTransmissions = [],
  mainTransmissionId = null,
  onUpdated = () => {},
}) {
  const [visible, setVisible] = useState([])
  const [toggledSummaries, setToggledSummaries] = useState([])
  const [updating, setUpdating] = useState(false)

  const { id: patientId } = patient

  const toggleSummary = useCallback(
    key => () => {
      if (toggledSummaries.includes(key)) {
        setToggledSummaries(toggledSummaries.filter(k => k !== key))
      } else {
        setToggledSummaries([...toggledSummaries, key])
      }
    },
    [toggledSummaries],
  )

  const { brandSuccess, blue, brandDangerMid, brandPrimary } = useTheme()
  const useStyles = createUseStyles({
    entries: {
      position: 'relative',
      fontSize: '.9em',
      maxHeight: '60vh', // increased height due to removed header bar
      overflowY: 'scroll',
      '&::after': {
        content: '""',
        background: `linear-gradient(to bottom, white, transparent 20px)`,
        height: '20px',
        width: '100%',
        position: 'absolute',
        top: 0,
      },
    },
    marginRight: {
      marginRight: '1em',
      display: 'inline-block',
    },
    green: {
      color: 'green',
    },
    orange: {
      color: 'orange',
    },
    miniBlock: {
      display: 'inline-block',
      width: '10px',
      height: '10px',
      marginRight: '.5em',
      borderRadius: '4px',
    },
    alertColor: {
      backgroundColor: `${brandDangerMid} !important`,
    },
    alertRow: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.2em',
      marginTop: 0,
    },
    alertLabel: {
      fontWeight: 600,
    },
    iconRowLabel: {
      fontWeight: 400,
    },
    alertInfo: {
      color: '#8c8c8c',
    },
    alertTag: {
      textTransform: 'uppercase',
      fontWeight: 600,
      minWidth: '64px',
      textAlign: 'center',
      marginLeft: 'auto',
    },
    criticalIcon: {
      color: 'red',
      marginRight: '4px',
      verticalAlign: 'middle',
    },
    officeCheckColor: {
      backgroundColor: `${brandSuccess} !important`,
    },
    receivedTransmissionsColor: {
      backgroundColor: `${blue} !important`,
    },
    remoteCheckColor: {
      backgroundColor: '#5175B3',
    },
    unarchivedTransmissionsColor: {
      backgroundColor: 'green !important',
    },
    entryDescription: {
      color: 'black',
      fontSize: '.9em',
    },
    signed: {
      marginLeft: '.5em',
    },
    filters: {
      paddingBottom: '.5em',
      borderBottom: '1px solid #e8e8e8',
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: '0.5em',
    },
    title: {
      color: brandPrimary,
      fontSize: '1.2em',
    },
    bottom: {
      marginBottom: '1em',
    },
    ok: {
      color: 'green',
    },
    notOk: {
      color: 'red',
    },
    entry: {
      display: 'grid',
      gridTemplateColumns: 'auto 1fr',
      gap: '.5em',
    },
    entrySummary: {
      display: 'inline-block',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      fontSize: '.8em',
      cursor: 'pointer',
      maxWidth: '80px',
    },
    entryOpen: {
      gridTemplateColumns: '1fr auto',
      gap: 0,
    },
    entrySummaryOpen: {
      gridColumn: '1 / span 2',
      display: 'block',
    },
    entrySummaryContainer: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-end',
    },
    diseaseList: {
      marginBottom: '1em',
    },
  })
  const classes = useStyles()

  const START_DATE = dayjs()
    .subtract(12, 'months')
    .startOf('month')
    .format('YYYY-MM-DD')

  const getAlerts = useCallback(
    async () => api.patients.getAlerts(patientId),
    [patientId],
  )
  const getOfficeChecks = useCallback(async () => {
    if (!practiceId) {
      return { body: [] }
    }

    return api.patients.getOfficeChecks(practiceId, patientId, {
      from_date: START_DATE,
      to_date: dayjs().format('YYYY-MM-DD'),
    })
  }, [patientId, practiceId, START_DATE])

  const getTransmissions = useCallback(
    async () => api.patients.getTransmissions(patientId),
    [patientId],
  )

  const getRemoteChecks = useCallback(
    async () => api.patients.getReports(patientId),
    [patientId],
  )

  const receivedTransmissions = useAsync(getTransmissions)
  const alerts = useAsync(getAlerts)
  const officeChecks = useAsync(getOfficeChecks)
  const previousRemoteChecks = useAsync(getRemoteChecks)

  const [diseases, setDiseases] = useState([])
  const [patientDetails, setPatientDetails] = useState(null)

  useEffect(() => {
    api.diseases.getDiseases(patientId).then(data => {
      const sortedData = data
        .sort((a, b) => a.name.localeCompare(b.name))
        .map(disease => disease.name)
      setDiseases(sortedData)
    })

    if (!patient?.medicalProvider) {
      api.patients.get(patientId).then(full => {
        setPatientDetails(full)
      })
    }
  }, [patient, patientId])

  const allEntries = useMemo(
    () =>
      [
        ...(previousRemoteChecks.value?.body || [])
          .filter(rc => rc.id !== remoteCheckId) // exclude current RC
          .map(entry => ({
            ...entry,
            date: entry.createdAt,
            type: 'remoteChecks',
          })),
        ...(receivedTransmissions.value?.body || []).map(entry => ({
          ...entry,
          type: 'receivedTransmissions',
        })),
        ...(alerts.value?.body || []).map(entry => ({
          ...entry,
          date: entry.createdAt,
          type: 'alerts',
        })),
        ...(officeChecks.value?.body || []).map(entry => ({
          ...entry,
          type: 'officeChecks',
        })),
      ]
        .map(entry => ({
          ...entry,
          date:
            entry?.createdAt || // Alerts
            entry?.cycleEndedAt || // Missed transmissions
            entry?.reportDate || // Received transmissions
            entry?.checkDate, // Office checks
        }))
        .sort((a, b) => {
          // main transmissions first within receivedTransmissions, but only for current remote check
          if (
            a.type === 'receivedTransmissions' &&
            a.main &&
            a.remoteCheckId === remoteCheckId
          )
            return -1
          if (
            b.type === 'receivedTransmissions' &&
            b.main &&
            b.remoteCheckId === remoteCheckId
          )
            return 1

          if (a.date > b.date) return -1
          if (a.date < b.date) return 1
          return 0
        }),
    [
      previousRemoteChecks.value,
      receivedTransmissions.value,
      alerts.value,
      officeChecks.value,
      remoteCheckId,
    ],
  )

  const visibleEntries = useMemo(
    () => allEntries.filter(entry => visible.includes(entry.type)),
    [allEntries, visible],
  )

  const getClass = useCallback(
    entry => {
      if (entry.type === 'alerts') return classes.alertColor
      if (entry.type === 'receivedTransmissions')
        return classes.receivedTransmissionsColor
      if (entry.type === 'officeChecks') return classes.officeCheckColor
      if (entry.type === 'remoteChecks') return classes.remoteCheckColor
      return 'red'
    },
    [classes],
  )

  const options = useMemo(
    () =>
      Object.keys(REPORTS)
        .filter(key => allEntries.filter(e => e.type === key).length > 0)
        .map(key => ({
          value: key,
          label: (
            <span>
              <span
                className={cn(classes.miniBlock, getClass({ type: key }))}
              />
              {`${REPORTS[key]} (${
                allEntries.filter(e => e.type === key).length
              })`}
            </span>
          ),
        })),
    [allEntries, classes.miniBlock, getClass],
  )

  useEffect(
    () =>
      setVisible(
        (() => {
          const allTypes = allEntries.reduce(
            (sum, entry) =>
              !sum.includes(entry.type) ? [...sum, entry.type] : sum,
            [],
          )

          // Default to hiding transmissions so the sidebar stays focused on
          // alerts/reports/office checks (users can opt-in by checking the box).
          const withoutTransmissions = allTypes.filter(
            type => type !== 'receivedTransmissions',
          )

          // If transmissions are the only available entries, don't hide everything.
          return withoutTransmissions.length > 0
            ? withoutTransmissions
            : allTypes
        })(),
      ),
    [allEntries, setVisible],
  )

  return (
    <div>
      <div className={classes.bottom}>
        <h3 className={classes.title}>Medical Provider</h3>
        <div style={{ marginBottom: '1em' }}>
          {(() => {
            const provider =
              patient?.medicalProvider || patientDetails?.medicalProvider
            return provider ? formatMedicalProvider(provider) : '—'
          })()}
        </div>
      </div>
      <div className={classes.bottom}>
        <h3 className={classes.title}>Diseases</h3>
        <Flex className={classes.diseaseList} gap="small" vertical>
          {[
            {
              key: 'afib_flutter',
              kind: 'icon',
              enabled: patient?.atrialFibrilationFlutterDisease,
              label: 'Atrial Fib./Flutter',
            },
            {
              key: 'prophylaxis',
              kind: 'icon',
              enabled: patient?.anticoagulatedDisease,
              label: 'Prophylaxis',
            },
            {
              key: 'oac_contraindicated',
              kind: 'tag',
              enabled: patient?.oacContraindicated,
              label: 'OAC Contraindicated',
              tooltip:
                'Indicates oral anticoagulation is contraindicated for this patient.',
            },
            {
              key: 'prn_anticoagulation',
              kind: 'icon',
              enabled: patient?.prnAnticoagulatedDisease,
              label: 'PRN Anticoagulation',
            },
            {
              key: 'alert_for_any_afib',
              kind: 'tag',
              enabled: patient?.alertForAnyAfib,
              label: 'Alert for Any AFib',
              tooltip:
                'Send alerts whenever any atrial fibrillation event is detected for this patient.',
            },
          ].map(item => {
            if (item.kind === 'tag') {
              return (
                <SidebarBooleanTag
                  key={item.key}
                  enabled={item.enabled}
                  label={item.label}
                  tooltip={item.tooltip}
                  classes={classes}
                />
              )
            }

            return (
              <SidebarBooleanIconRow
                key={item.key}
                enabled={item.enabled}
                label={item.label}
                classes={classes}
              />
            )
          })}
        </Flex>
        <Flex className={classes.diseaseList} gap="small" vertical>
          {diseases.map(disease => (
            <span key={disease}>{disease}</span>
          ))}
        </Flex>
      </div>
      <h3 className={classes.title}>Reports, alerts & office checks</h3>
      <Checkbox.Group
        options={options}
        onChange={setVisible}
        value={visible}
        className={classes.filters}
      />
      <div className={classes.entries}>
        <List
          size="small"
          dataSource={visibleEntries}
          renderItem={(entry, index) => {
            const isVisible = toggledSummaries.includes(entry.id)
            const isIncluded = includedTransmissions.includes(
              entry.transmissionId || entry.id,
            )
            const isMain =
              mainTransmissionId === (entry.transmissionId || entry.id)
            const isOnlyTransmission = includedTransmissions.length === 1

            const handleAddRemove = async action => {
              if (!remoteCheckId) return
              setUpdating(true)
              try {
                const transmissionId = entry.transmissionId || entry.id
                if (action === 'remove') {
                  await api.remoteChecks.removeTransmission(
                    remoteCheckId,
                    transmissionId,
                  )
                } else {
                  await api.remoteChecks.addTransmission(
                    remoteCheckId,
                    transmissionId,
                    false,
                  )
                }
                onUpdated()
              } finally {
                setUpdating(false)
              }
            }
            const handleSetMain = async () => {
              if (!remoteCheckId) return
              setUpdating(true)
              try {
                await api.remoteChecks.addTransmission(
                  remoteCheckId,
                  entry.transmissionId || entry.id,
                  true,
                )
                onUpdated()
              } finally {
                setUpdating(false)
              }
            }

            const isExcluded =
              (entry.type === 'receivedTransmissions' &&
                entry.remoteCheckId !== remoteCheckId &&
                entry.remoteCheckId) ||
              entry.type !== 'receivedTransmissions'

            return (
              <List.Item>
                <List.Item.Meta
                  title={
                    <div
                      className={cn(classes.entry, {
                        [classes.entryOpen]: isVisible,
                      })}
                    >
                      {remoteCheckId && !isExcluded && (
                        <span>
                          {isIncluded ? (
                            <>
                              {!isOnlyTransmission && (
                                <Button
                                  size="small"
                                  icon={<CloseOutlined />}
                                  type="link"
                                  disabled={updating}
                                  onClick={() => handleAddRemove('remove')}
                                  title="Remove from report"
                                />
                              )}
                              {!isMain && (
                                <Button
                                  size="small"
                                  icon={<StarOutlined />}
                                  type="link"
                                  disabled={updating}
                                  onClick={handleSetMain}
                                  title="Mark as main"
                                />
                              )}
                              {isMain && (
                                <Tooltip title="Main transmission">
                                  <StarOutlined
                                    style={{
                                      color: 'green',
                                      marginRight: '4px',
                                    }}
                                  />
                                </Tooltip>
                              )}
                            </>
                          ) : (
                            <Button
                              size="small"
                              icon={<LeftOutlined />}
                              type="link"
                              disabled={updating}
                              onClick={() => handleAddRemove('add')}
                              title="Add to this report"
                            />
                          )}
                        </span>
                      )}
                      <span>
                        <a
                          href={getLink(entry)}
                          rel="noopener noreferrer"
                          target="_blank"
                          className={classes.entryLink}
                          style={{
                            marginLeft: isExcluded ? '1.9rem' : '0',
                          }}
                        >
                          {entry.critical && (
                            <Tooltip title="Requires Attention">
                              <ExclamationCircleOutlined
                                className={classes.criticalIcon}
                              />
                            </Tooltip>
                          )}
                          <span
                            className={cn(classes.miniBlock, getClass(entry))}
                          />
                          {dayjs(entry.date).format('MMM Do YYYY')}
                          {'signedAt' in entry && (
                            <span className={classes.signed}>
                              {entry.signedAt ? (
                                <Tag color="green">Signed</Tag>
                              ) : (
                                <Tag color="orange">Not signed</Tag>
                              )}
                            </span>
                          )}
                        </a>
                      </span>
                      {isVisible && (
                        <Button
                          icon={<UpOutlined />}
                          size="small"
                          type="link"
                          className={classes.entrySummaryClose}
                          onClick={toggleSummary(entry.id)}
                        />
                      )}
                      <div className={classes.entrySummaryContainer}>
                        <span
                          className={cn({
                            [classes.entrySummary]: !isVisible,
                            [classes.entrySummaryOpen]: isVisible,
                          })}
                          onClick={toggleSummary(entry.id)}
                          onKeyDown={toggleSummary(entry.id)}
                          role="button"
                          tabIndex={index}
                        >
                          {entry?.summary}
                        </span>
                        {!isVisible && !!entry?.summary && (
                          <Button
                            icon={<DownOutlined />}
                            size="small"
                            type="link"
                            className={classes.entrySummaryClose}
                            onClick={toggleSummary(entry.id)}
                          />
                        )}
                      </div>
                      {isVisible && (
                        <Flex justify="center">
                          <Copy text={entry?.summary} />
                        </Flex>
                      )}
                    </div>
                  }
                />
              </List.Item>
            )
          }}
        />
      </div>
    </div>
  )
}

TransmissionSidebar.propTypes = {
  patient: PropTypes.shape({ id: PropTypes.string }).isRequired,
  practiceId: PropTypes.string.isRequired,
  remoteCheckId: PropTypes.string,
  includedTransmissions: PropTypes.arrayOf(PropTypes.string),
  mainTransmissionId: PropTypes.string,
  onUpdated: PropTypes.func,
}

export default TransmissionSidebar
